import { Row, Tooltip, Text, Icon, Column } from "@bridgesplit/ui";
import { formatPercent } from "@bridgesplit/utils";
import { Divider } from "@mui/material";
import {
    calculateLeverageMultiplier,
    formatLeverage,
    LoopPositionExpanded,
    useLoanMultipliersUtil,
    useFarmingInfoByActionTypeAndAddress,
    ActionType,
    FarmingInfoExpanded,
    BsMetaUtil
} from "@bridgesplit/abf-react";

import { TooltipContent } from "../..";
import { BaseApy } from "../BaseApy";
import { GenericRewardsApyProps } from "../types";
import { RewardsDailyPointsBreakdown, RewardsDailyCustomPoints } from "../daily-rewards";
import { useRewardsColor } from "../util";
import { ApyBreakdownProps, ApyBreakdownTooltipContent, RateDisplay } from "../common";
import { RewardsProtocolPoints } from "../RewardsProtocolPoints";

export function LoopPortfolioApy(props: LoopPositionApyTooltipProps) {
    const netApy = props.loopPosition?.loanExpanded?.netApyLive;

    const { loanMultipliers, isLoading } = useLoanMultipliersUtil();
    const { farmingInfo, hasFarmingInfo } = useFarmingInfoByActionTypeAndAddress(
        props.loopPosition?.loopExpanded?.vaultIdentifier,
        ActionType.Loop
    );
    const color = useRewardsColor(loanMultipliers, props.identifier ? [props.identifier] : undefined, [
        props.loopscalePointsAction
    ]);

    return (
        <Tooltip
            noPadding
            title={<LoopPositionApyTooltip {...props} farmingInfo={farmingInfo} hasFarmingInfo={hasFarmingInfo} />}
            reverseColors
        >
            <Row spacing={0.5}>
                <Text sx={{ color }} loading={isLoading}>
                    <Icon type="points" />
                </Text>
                <Row spacing={0.5}>
                    <RateDisplay apy={netApy} variant="body1" color="body" isFeatured={true} />
                    {!props.hideApyText && (
                        <Text variant="body1" color="caption">
                            APY
                        </Text>
                    )}
                </Row>
            </Row>
        </Tooltip>
    );
}

interface LoopPositionApyTooltipProps extends GenericRewardsApyProps {
    customDailyPoints: number | undefined;
    loopPosition: LoopPositionExpanded | undefined;
}

interface LoopPositionApyTooltipInternalProps extends LoopPositionApyTooltipProps {
    farmingInfo: FarmingInfoExpanded | undefined;
    hasFarmingInfo: boolean;
}

function LoopPositionApyTooltip(props: LoopPositionApyTooltipInternalProps) {
    const loanExpanded = props.loopPosition?.loanExpanded;
    const borrowApy = loanExpanded?.borrowerWAvgApy;
    const baseApy = props.loopPosition?.loopExpanded?.loopVault.collateralApyPct;
    const leverage = calculateLeverageMultiplier(loanExpanded);

    return (
        <TooltipContent px={0} py={1.5} spacing={1}>
            <Column px={1.5} spacing={0.5}>
                <BaseApy {...props} caption={leverage ? `Net APY` : undefined} variant="body2" />
                <Column sx={{ paddingLeft: 1.5 }}>
                    <LoopApyBreakdown
                        collateralSymbol={BsMetaUtil.getSymbol(props.loopPosition?.loopExpanded?.collateralToken, {
                            shortSymbol: true
                        })}
                        baseApy={baseApy}
                        borrowApy={borrowApy}
                        leverage={leverage}
                    />
                </Column>
            </Column>
            <Divider />
            <Column px={1.5} spacing={0.5}>
                <RewardsDailyCustomPoints
                    loopscalePointsAction={props.loopscalePointsAction}
                    identifier={props.identifier}
                    customDailyPoints={props.customDailyPoints}
                />
                <Column sx={{ paddingLeft: 1.5 }}>
                    <RewardsDailyPointsBreakdown {...props} />
                </Column>
            </Column>

            {props.hasFarmingInfo && (
                <>
                    <Divider />
                    <Column px={1.5}>
                        <RewardsProtocolPoints farmingInfo={props.farmingInfo} />
                    </Column>
                </>
            )}
        </TooltipContent>
    );
}

function LoopApyBreakdown({
    collateralSymbol,
    baseApy,
    borrowApy,
    leverage
}: {
    collateralSymbol: string | undefined;
    baseApy: number | undefined;
    borrowApy: number | undefined;
    leverage: number | undefined;
}) {
    const breakdown: ApyBreakdownProps = [
        {
            label: `Base ${collateralSymbol} APY`,
            value: formatPercent(baseApy),
            icon: "trending-up"
        },
        {
            label: "Borrow APY",
            value: formatPercent(borrowApy),
            icon: "currency-exchange"
        },
        {
            label: "Leverage",
            value: formatLeverage(leverage),
            icon: "leverage"
        }
    ];

    return <ApyBreakdownTooltipContent breakdown={breakdown} />;
}
